import { createAsyncThunk } from '@reduxjs/toolkit';

import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { AdminApiPaths } from '../../../api/APIPaths';
import { ILessonTutorFeedback, ILessonTutorFeedbackRewardData } from '.';

export type TAdminCreateLessonTutorFeedback = {
  student: string;
  tutors: string[];
  section: string;
  isStudentAttended: 'Yes' | 'No';
  lessonExtension?: number;
  lessonExtensionReason?: string;
  studentAttentiveness?: string;
  graspingAndApplicationOfConceptTaught?: string;
  progressForThisTopicAndSyllabus?: string;
  homeworkCompletedFromPreviousLesson?: string;
  homeworkSetForThisLesson?: 'Yes' | 'No';
  quizScore?: number;
  quizScoreOutOf?: number;
  overallStudentRating?: number;
  feedbackRewards?: string[];
  comments?: string;
};

export type TAdminGetLessonTutorFeedbacks = {
  limit: number;
  offset: number;
  section?: string;
};

export type TAdminUpdateLessonTutorFeedback = {
  id: string;
  isStudentAttended: 'Yes' | 'No';
  lessonExtension?: number;
  lessonExtensionReason?: string;
  studentAttentiveness?: string;
  graspingAndApplicationOfConceptTaught?: string;
  progressForThisTopicAndSyllabus?: string;
  homeworkCompletedFromPreviousLesson?: string;
  homeworkSetForThisLesson?: 'Yes' | 'No';
  quizScore?: number;
  quizScoreOutOf?: number;
  overallStudentRating?: number;
  feedbackRewards?: string[];
  comments?: string;
};

const apiDataProvider = new APIDataProvider();

export const adminGetLessonTutorFeedbackRewards = createAsyncThunk(
  '/admin/lesson-tutor-feedbacks/feedback-rewards',
  async () => {
    const path = `${AdminApiPaths.lessonTutorFeedbacks}/feedback-rewards`;

    const response = await apiDataProvider.get<
      TApiResponse<ILessonTutorFeedbackRewardData>
    >(path);

    return response.data;
  }
);

export const adminCreateLessonTutorFeedback = createAsyncThunk(
  '/admin/lesson-tutor-feedbacks/create',
  async (data: TAdminCreateLessonTutorFeedback) => {
    const path = `${AdminApiPaths.lessonTutorFeedbacks}`;

    const response = await apiDataProvider.post<
      TApiResponse<ILessonTutorFeedback[]>
    >(path, data);

    return response.data;
  }
);

export const adminGetLessonTutorFeedbacks = createAsyncThunk(
  '/admin/lesson-tutor-feedbacks/list',
  async ({ limit, offset, section }: TAdminGetLessonTutorFeedbacks) => {
    let path = `${AdminApiPaths.lessonTutorFeedbacks}?limit=${limit}&offset=${offset}`;

    if (section) {
      path += `&section=${section}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ILessonTutorFeedback>>
    >(path);

    return response.data;
  }
);

export const adminUpdateLessonTutorFeedbacks = createAsyncThunk(
  '/admin/lesson-tutor-feedbacks/update-one',
  async (data: TAdminUpdateLessonTutorFeedback) => {
    const path = `${AdminApiPaths.lessonTutorFeedbacks}/${data.id}`;

    const response = await apiDataProvider.put<
      TApiResponse<ILessonTutorFeedback>
    >(path, data);

    return response.data;
  }
);

export const adminDeleteLessonTutorFeedback = createAsyncThunk(
  '/admin/lesson-tutor-feedbacks/delete-one',
  async ({ id }: { id: string }) => {
    const path = `${AdminApiPaths.lessonTutorFeedbacks}/${id}`;

    const response = await apiDataProvider.delete<
      TApiResponse<ILessonTutorFeedback>
    >(path);

    return response.data;
  }
);
